import React, { useEffect, useState, useRef } from 'react';
import usePlacesAutocomplete, { getGeocode, getLatLng } from 'use-places-autocomplete';
import { useGoogleMapsApi } from '@/hooks/useGoogleMapsApi';
import { Controller, Control } from 'react-hook-form';

export interface ParsedAddress {
    fullAddress: string;
    street: string;
    number: string;
    neighborhood: string;
    city: string;
    state: string;
    country: string;
    zipCode: string;
    latitude: number;
    longitude: number;
}

export interface GooglePlacesInputProps {
    types?: string[];
    onSelectAddress: (address: ParsedAddress) => void;
    initialValue?: string;
    disabled?: boolean;
    placeholder?: string;
    className?: string;
    'data-testid'?: string;
}

export interface GooglePlacesControlledInputProps {
    types?: string[];
    name: string;
    control: Control<any>;
    onSelectAddress: (address: ParsedAddress) => void;
    disabled?: boolean;
    placeholder?: string;
    className?: string;
    'data-testid'?: string;
}

/**
 * Componente interno simplificado para uso standalone (não React Hook Form)
 */
const PlacesAutocompleteInput: React.FC<GooglePlacesInputProps> = ({
    onSelectAddress,
    initialValue = '',
    disabled = false,
    placeholder = 'Digite o endereço...',
    className = 'input-primary w-full',
    types,
    'data-testid': testId
}) => {
    // Estado controlado simples
    const [inputValue, setInputValue] = useState(initialValue || '');

    return (
        <ControlledPlacesInput
            field={{
                value: inputValue,
                onChange: setInputValue,
                onBlur: () => { },
            }}
            onSelectAddress={onSelectAddress}
            disabled={disabled}
            placeholder={placeholder}
            className={className}
            types={types}
            data-testid={testId}
        />
    );
};

/**
 * Componente interno controlado pelo React Hook Form
 * Integração direta sem estado interno complexo para evitar controlled/uncontrolled issues
 */
const ControlledPlacesInput: React.FC<{
    field: any;
    onSelectAddress: (address: ParsedAddress) => void;
    disabled?: boolean;
    placeholder?: string;
    className?: string;
    types?: string[];
    'data-testid'?: string;
}> = ({
    field,
    onSelectAddress,
    disabled = false,
    placeholder = 'Digite o endereço...',
    className = 'input-primary w-full',
    types,
    'data-testid': testId
}) => {
        // Estados para navegação por teclado
        const [selectedIndex, setSelectedIndex] = useState(-1);
        const [isGeocoding, setIsGeocoding] = useState(false);
        const inputRef = useRef<HTMLInputElement>(null);

        // Hook places autocomplete - API já está carregada quando chegamos aqui
        const {
            ready,
            suggestions: { status, data },
            setValue: setPlacesValue,
            clearSuggestions,
        } = usePlacesAutocomplete({
            requestOptions: {
                componentRestrictions: { country: 'br' },
                types: types ?? ['address']
            },
            debounce: 300,
            defaultValue: field.value || '',
        });

        // Reset selectedIndex quando as sugestões mudam
        useEffect(() => {
            setSelectedIndex(-1);
        }, [data]);

        const parseAddressComponents = (addressComponents: any[]): Partial<ParsedAddress> => {
            const getAddressComponent = (type: string, useShortName = false) => {
                const component = addressComponents.find(c => c.types.includes(type));
                return component ? (useShortName ? component.short_name : component.long_name) : '';
            };

            return {
                street: getAddressComponent('route'),
                number: getAddressComponent('street_number'),
                neighborhood: getAddressComponent('sublocality_level_1'),
                city: getAddressComponent('administrative_area_level_2'),
                state: getAddressComponent('administrative_area_level_1', true),
                country: getAddressComponent('country', true),
                zipCode: getAddressComponent('postal_code'),
            };
        };

        const handleSelectSuggestion = async (suggestion: any) => {
            // Previne seleção da mesma sugestão
            if (field.value === suggestion.description) {
                clearSuggestions();
                setSelectedIndex(-1);
                return;
            }

            // Atualizar React Hook Form
            field.onChange(suggestion.description);
            clearSuggestions();
            setSelectedIndex(-1);

            setIsGeocoding(true);

            try {
                const results = await getGeocode({ address: suggestion.description });
                const { lat, lng } = await getLatLng(results[0]);

                const addressComponents = results[0].address_components;
                const parsedComponents = parseAddressComponents(addressComponents);

                const parsedAddress: ParsedAddress = {
                    fullAddress: suggestion.description,
                    latitude: lat,
                    longitude: lng,
                    street: parsedComponents.street || '',
                    number: parsedComponents.number || '',
                    neighborhood: parsedComponents.neighborhood || '',
                    city: parsedComponents.city || '',
                    state: parsedComponents.state || '',
                    country: parsedComponents.country || '',
                    zipCode: parsedComponents.zipCode || '',
                };

                onSelectAddress(parsedAddress);
            } catch (error) {
                console.error('❌ Erro ao geocodificar endereço:', error);
                const basicAddress: ParsedAddress = {
                    fullAddress: suggestion.description,
                    street: '', number: '', neighborhood: '', city: '', state: '', country: '', zipCode: '',
                    latitude: 0, longitude: 0,
                };
                onSelectAddress(basicAddress);
            } finally {
                setIsGeocoding(false);
            }
        };

        const handleClear = () => {
            field.onChange('');
            clearSuggestions();
            setSelectedIndex(-1);

            const emptyAddress: ParsedAddress = {
                fullAddress: '', street: '', number: '', neighborhood: '', city: '', state: '', country: '', zipCode: '',
                latitude: 0, longitude: 0,
            };
            onSelectAddress(emptyAddress);
        };

        const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            const newValue = e.target.value;
            field.onChange(newValue);  // Atualizar React Hook Form
            setSelectedIndex(-1);

            // Sincronizar com places autocomplete
            if (ready) {
                setPlacesValue(newValue, true);
            }

            // Notificar componente pai com endereço parcial
            const partialAddress: ParsedAddress = {
                fullAddress: newValue,
                street: '', number: '', neighborhood: '', city: '', state: '', country: '', zipCode: '',
                latitude: 0, longitude: 0,
            };
            onSelectAddress(partialAddress);
        };

        const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
            if (!ready || data.length === 0) return;

            switch (e.key) {
                case 'ArrowDown':
                    e.preventDefault();
                    setSelectedIndex(prev => prev < data.length - 1 ? prev + 1 : 0);
                    break;
                case 'ArrowUp':
                    e.preventDefault();
                    setSelectedIndex(prev => prev > 0 ? prev - 1 : data.length - 1);
                    break;
                case 'Enter':
                    e.preventDefault();
                    if (selectedIndex >= 0 && selectedIndex < data.length) {
                        handleSelectSuggestion(data[selectedIndex]);
                    }
                    break;
                case 'Escape':
                    clearSuggestions();
                    setSelectedIndex(-1);
                    inputRef.current?.blur();
                    break;
            }
        };

        // Lógica do loading separada por cenários:
        // 1. API não pronta
        // 2. Fazendo geocoding após seleção  
        // 3. Pesquisando (digitou pouco texto mas sem resultados ainda)
        // Endereços completos (>30 chars) indicam seleção, não pesquisa ativa
        const isSearching = ready && field.value && field.value.length > 2 && field.value.length <= 30 && data.length === 0 && status !== 'ZERO_RESULTS';
        const shouldShowLoadingSpinner = (!ready || isGeocoding || isSearching) && !(ready && field.value && !isGeocoding);
        const shouldShowClearButton = ready && field.value && field.value.length > 0 && !disabled && !shouldShowLoadingSpinner;

        return (
            <div className="relative w-full">
                <input
                    ref={inputRef}
                    value={field.value || ''}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    onBlur={field.onBlur}
                    disabled={disabled || !ready}
                    placeholder={ready ? placeholder : 'Aguardando Google Maps...'}
                    className={`${className} ${disabled || !ready ? 'opacity-50 cursor-not-allowed' : ''} ${shouldShowLoadingSpinner || shouldShowClearButton ? 'pr-12' : ''}`}
                    data-testid={testId}
                    autoComplete="off"
                />

                {shouldShowClearButton && (
                    <button
                        type="button"
                        onClick={handleClear}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none focus:text-gray-600 transition-colors duration-200"
                        aria-label="Limpar endereço"
                        data-testid="clear-address-button"
                    >
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                )}

                {ready && status === 'OK' && data.length > 0 && (
                    <ul className="absolute z-50 w-full bg-white border border-gray-300 rounded-md mt-1 max-h-60 overflow-auto shadow-lg">
                        {data.map((suggestion, index) => (
                            <li
                                key={suggestion.place_id}
                                onClick={() => handleSelectSuggestion(suggestion)}
                                className={`p-3 cursor-pointer border-b border-gray-100 last:border-b-0 text-sm transition-colors duration-150 ${index === selectedIndex
                                    ? 'bg-indigo-50 border-indigo-200'
                                    : 'hover:bg-gray-100'
                                    }`}
                            >
                                <div className="font-medium text-gray-900">
                                    {suggestion.structured_formatting.main_text}
                                </div>
                                <div className="text-gray-500 text-xs">
                                    {suggestion.structured_formatting.secondary_text}
                                </div>
                            </li>
                        ))}
                    </ul>
                )}

                {shouldShowLoadingSpinner && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-600"></div>
                    </div>
                )}
            </div>
        );
    };

/**
 * Componente wrapper controlado pelo React Hook Form
 * Resolve o problema de controlled/uncontrolled input usando estratégia simplificada
 */
export const GooglePlacesControlledInput: React.FC<GooglePlacesControlledInputProps> = ({
    name,
    control,
    onSelectAddress,
    disabled = false,
    placeholder = 'Digite o endereço...',
    className = 'input-primary w-full',
    types,
    'data-testid': testId
}) => {
    const { isLoaded, isLoading, error } = useGoogleMapsApi();

    return (
        <Controller
            name={name}
            control={control}
            defaultValue=""  // ✅ SEMPRE fornecer defaultValue
            render={({ field }) => {
                // Estados de carregamento
                if (isLoading) {
                    return (
                        <div className="relative">
                            <input
                                type="text"
                                value=""  // ✅ Valor fixo durante loading
                                onChange={() => { }}
                                disabled
                                placeholder="Carregando Google Maps..."
                                className={`${className} opacity-50 cursor-not-allowed`}
                                data-testid={testId}
                            />
                            <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-600"></div>
                            </div>
                        </div>
                    );
                }

                if (error) {
                    return (
                        <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                            <div className="text-sm text-red-800">
                                Erro ao carregar Google Maps: {error.message}
                            </div>
                        </div>
                    );
                }

                if (!isLoaded) {
                    return (
                        <div className="relative">
                            <input
                                type="text"
                                value=""  // ✅ Valor fixo durante aguardo
                                onChange={() => { }}
                                disabled
                                placeholder="Aguardando Google Maps..."
                                className={`${className} opacity-50 cursor-not-allowed`}
                                data-testid={testId}
                            />
                            <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-400"></div>
                            </div>
                        </div>
                    );
                }

                // ✅ API carregada - renderizar componente controlado diretamente
                return (
                    <ControlledPlacesInput
                        field={field}
                        onSelectAddress={onSelectAddress}
                        disabled={disabled}
                        placeholder={placeholder}
                        className={className}
                        types={types}
                        data-testid={testId}
                    />
                );
            }}
        />
    );
};

/**
 * Componente wrapper que controla quando renderizar o input
 * Segue o padrão recomendado pela documentação do use-places-autocomplete
 * 
 * ✨ CARACTERÍSTICAS:
 * - Lazy loading automático da API do Google Maps
 * - Estado global compartilhado entre todas as instâncias
 * - Renderização condicional baseada no estado da API
 * - Falha explícita se API não puder ser carregada
 * 
 * 🚀 USO SIMPLES:
 * <GooglePlacesInput onSelectAddress={handleAddress} />
 */
const GooglePlacesInput: React.FC<GooglePlacesInputProps> = (props) => {
    // Hook autocontido que gerencia lazy loading da API globalmente
    const { isLoaded, isLoading, error } = useGoogleMapsApi();

    // Estados de carregamento e erro
    if (isLoading) {
        return (
            <div className="relative">
                <input
                    type="text"
                    value={props.initialValue || ''}
                    onChange={() => { }} // Noop durante loading
                    disabled
                    placeholder="Carregando Google Maps..."
                    className={`${props.className || 'input-primary w-full'} opacity-50 cursor-not-allowed`}
                    data-testid={props['data-testid']}
                />
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-600"></div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                <div className="flex items-center">
                    <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                        </svg>
                    </div>
                    <div className="ml-3">
                        <h3 className="text-sm font-medium text-red-800">
                            Erro ao carregar Google Maps
                        </h3>
                        <div className="mt-2 text-sm text-red-700">
                            <p>{error.message}</p>
                            <p className="mt-1 text-xs">Verifique a configuração da chave da API no arquivo .env</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (!isLoaded) {
        // Estado aguardando - raro mas possível
        return (
            <div className="relative">
                <input
                    type="text"
                    value={props.initialValue || ''}
                    onChange={() => { }} // Noop durante aguardo
                    disabled
                    placeholder="Aguardando Google Maps..."
                    className={`${props.className || 'input-primary w-full'} opacity-50 cursor-not-allowed`}
                    data-testid={props['data-testid']}
                />
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-400"></div>
                </div>
            </div>
        );
    }

    // 🎯 API carregada! Renderizar o componente que usa usePlacesAutocomplete
    return <PlacesAutocompleteInput {...props} />;
};

export default GooglePlacesInput;